import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Car, ArrowLeft, CheckCircle, Upload, Loader2, Package, Camera, User, FileText, CreditCard, ImageIcon, Square, CheckSquare, X, Gift, XCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const DriverSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState({});
  const [driverId, setDriverId] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referralValidation, setReferralValidation] = useState(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    paypalEmail: '',
    einSsn: '',
    vehicleType: '',
    vehicleModel: '',
    vehicleYear: '',
    licensePlate: '',
    serviceTypes: []
  });
  
  // Fetch vehicle types from API on component mount
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setIsLoadingVehicles(true);
        const response = await axios.get(`${BACKEND_URL}/api/drivers/vehicle-types`);
        const types = response.data.vehicle_types || [];
        setVehicleTypes(types);
      } catch (error) {
        console.error('Error fetching vehicle types:', error);
        // Fallback to default types
        setVehicleTypes([
          { name: 'Sedan', description: 'Standard sedan' },
          { name: 'SUV', description: 'Sport utility vehicle' },
          { name: 'Van', description: 'Passenger van' }
        ]);
      } finally {
        setIsLoadingVehicles(false);
      }
    };
    fetchVehicleTypes();
  }, [BACKEND_URL]);

  // Check for referral code in URL params on mount
  useEffect(() => {
    const refCode = searchParams.get('ref') || searchParams.get('referral');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Auto-validate the code
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValidation(null);
      return;
    }
    
    setIsValidatingReferral(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/driver/validate/${code.toUpperCase()}`);
      setReferralValidation(response.data);
    } catch (error) {
      setReferralValidation({ valid: false, message: 'Error validating code' });
    } finally {
      setIsValidatingReferral(false);
    }
  };

  // Debounce referral code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (referralCode) {
        validateReferralCode(referralCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);
  
  // Terms & Conditions state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState({ title: 'Terms & Conditions', content: '' });
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  
  // Photo states with previews
  const [photos, setPhotos] = useState({
    profilePhoto: null,
    licensePhoto: null,
    vehicleFront: null,
    vehicleRear: null
  });
  const [photoUrls, setPhotoUrls] = useState({
    profilePhoto: null,
    licensePhoto: null,
    vehicleFront: null,
    vehicleRear: null
  });
  const [uploadedUrls, setUploadedUrls] = useState({
    profilePhoto: null,
    licensePhoto: null,
    vehicleFront: null,
    vehicleRear: null
  });

  // File input refs
  const profilePhotoRef = useRef(null);
  const licensePhotoRef = useRef(null);
  const vehicleFrontRef = useRef(null);
  const vehicleRearRef = useRef(null);

  const handleServiceTypeToggle = (type) => {
    setFormData(prev => {
      const types = prev.serviceTypes.includes(type)
        ? prev.serviceTypes.filter(t => t !== type)
        : [...prev.serviceTypes, type];
      return { ...prev, serviceTypes: types };
    });
  };

  // Photo preview handler (instant preview before upload)
  const handlePhotoSelect = (e, photoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image under 5MB",
        variant: "destructive"
      });
      return;
    }
    
    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPhotos(prev => ({ ...prev, [photoType]: file }));
    setPhotoUrls(prev => ({ ...prev, [photoType]: previewUrl }));
  };

  // Fetch Terms & Conditions
  const fetchTermsConditions = async () => {
    try {
      setIsLoadingTerms(true);
      const response = await axios.get(`${BACKEND_URL}/api/terms`);
      setTermsContent({
        title: response.data.title || 'Terms & Conditions',
        content: response.data.content || ''
      });
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // Open Terms modal
  const openTermsModal = () => {
    fetchTermsConditions();
    setShowTermsModal(true);
  };

  // Upload a single photo to backend
  const uploadPhoto = async (photoType, endpoint) => {
    if (!photos[photoType] || !driverId) return null;
    
    setIsUploading(prev => ({ ...prev, [photoType]: true }));
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', photos[photoType]);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/drivers/${driverId}/upload/${endpoint}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadedUrls(prev => ({ ...prev, [photoType]: response.data.url }));
      return response.data.url;
    } catch (error) {
      console.error(`Error uploading ${photoType}:`, error);
      throw error;
    } finally {
      setIsUploading(prev => ({ ...prev, [photoType]: false }));
    }
  };

  // Submit Step 1 & 2 - Basic Info and Vehicle Info
  const handleBasicInfoSubmit = async () => {
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.phone || !formData.password) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return false;
    }
    
    if (formData.serviceTypes.length === 0) {
      toast({
        title: "Service Type Required",
        description: "Please select at least one service type",
        variant: "destructive"
      });
      return false;
    }

    // Validate Terms & Conditions acceptance
    if (!termsAccepted) {
      toast({
        title: "Terms & Conditions Required",
        description: "You must accept the Terms & Conditions to register",
        variant: "destructive"
      });
      return false;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/drivers/signup`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        paypal_email: formData.paypalEmail || formData.email,
        ein_ssn: formData.einSsn,
        vehicle_type: formData.vehicleType,
        vehicle_model: formData.vehicleModel,
        vehicle_year: formData.vehicleYear,
        license_plate: formData.licensePlate,
        service_types: formData.serviceTypes,
        terms_accepted: termsAccepted
      });
      
      // Store the driver ID for photo uploads
      const newDriverId = response.data.driver_id;
      setDriverId(newDriverId);
      
      // Apply referral code if provided and valid
      if (referralCode && referralValidation?.valid) {
        try {
          await axios.post(`${BACKEND_URL}/api/referral/driver/apply`, {
            referral_code: referralCode.toUpperCase(),
            driver_id: newDriverId
          });
          toast({
            title: "Referral Applied!",
            description: `Referred by ${referralValidation.referrer_name}. Complete rides to earn your bonus!`,
          });
        } catch (refError) {
          console.error('Referral apply error:', refError);
          // Don't fail signup if referral fails
        }
      }
      
      toast({
        title: "Profile Created!",
        description: "Now let's upload your required documents.",
      });
      
      return true;
    } catch (error) {
      console.error('Signup error:', error);
      toast({
        title: "Signup Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle step navigation
  const handleNextStep = async () => {
    if (step === 1) {
      // Validate Step 1 - Personal Information
      if (!formData.fullName || !formData.email || !formData.phone || !formData.password) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields",
          variant: "destructive"
        });
        return;
      }
      
      if (formData.serviceTypes.length === 0) {
        toast({
          title: "Service Type Required",
          description: "Please select at least one service type",
          variant: "destructive"
        });
        return;
      }

      // Validate Terms & Conditions acceptance in Step 1
      if (!termsAccepted) {
        toast({
          title: "Terms & Conditions Required",
          description: "You must accept the Terms & Conditions to continue",
          variant: "destructive"
        });
        return;
      }
      
      setStep(2);
    } else if (step === 2) {
      // Validate vehicle info
      if (!formData.vehicleType || !formData.vehicleModel || !formData.vehicleYear || !formData.licensePlate) {
        toast({
          title: "Missing Vehicle Information",
          description: "Please fill in all vehicle details",
          variant: "destructive"
        });
        return;
      }
      
      // Submit basic info and vehicle info to create driver
      const success = await handleBasicInfoSubmit();
      if (success) {
        setStep(3);
      }
    } else if (step === 3) {
      // Validate EIN/SSN - must be at least 9 digits (without hyphens)
      const digitsOnly = formData.einSsn.replace(/[^0-9]/g, '');
      if (!formData.einSsn || digitsOnly.length < 9) {
        toast({
          title: "Tax Information Required",
          description: "Please enter your full 9-digit EIN or Social Security Number",
          variant: "destructive"
        });
        return;
      }
      
      // Update EIN/SSN in database
      try {
        await axios.put(`${BACKEND_URL}/api/drivers/${driverId}/profile`, {
          ein_ssn: formData.einSsn
        });
        setStep(4);
      } catch (error) {
        console.error('EIN/SSN save error:', error.response?.data);
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to save tax information. Please try again.",
          variant: "destructive"
        });
      }
    } else if (step === 4) {
      // Validate photos selected
      if (!photos.profilePhoto || !photos.licensePhoto) {
        toast({
          title: "Photos Required",
          description: "Please upload your profile photo and driver's license",
          variant: "destructive"
        });
        return;
      }
      
      // Upload photos
      setIsSubmitting(true);
      try {
        await uploadPhoto('profilePhoto', 'profile-photo');
        await uploadPhoto('licensePhoto', 'license-photo');
        toast({
          title: "Documents Uploaded!",
          description: "Now let's add your vehicle photos.",
        });
        setStep(5);
      } catch (error) {
        toast({
          title: "Upload Failed",
          description: "Failed to upload documents. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    } else if (step === 5) {
      // Validate vehicle photos
      if (!photos.vehicleFront || !photos.vehicleRear) {
        toast({
          title: "Vehicle Photos Required",
          description: "Please upload front and rear photos of your vehicle",
          variant: "destructive"
        });
        return;
      }
      
      // Upload vehicle photos
      setIsSubmitting(true);
      try {
        await uploadPhoto('vehicleFront', 'vehicle-front');
        await uploadPhoto('vehicleRear', 'vehicle-rear');
        
        toast({
          title: "Registration Complete!",
          description: "Your application is under review. You'll be notified when approved."
        });
        
        setTimeout(() => navigate('/driver/login'), 2000);
      } catch (error) {
        toast({
          title: "Upload Failed",
          description: "Failed to upload vehicle photos. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Photo upload component
  const PhotoUploadBox = ({ label, photoType, inputRef, icon: Icon, description, isRequired = true }) => (
    <div>
      <Label className="text-sm font-medium">
        {label} {isRequired && '*'}
      </Label>
      <div 
        onClick={() => inputRef.current?.click()}
        className={`mt-2 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
          photoUrls[photoType] 
            ? 'border-green-400 bg-green-50' 
            : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50'
        }`}
      >
        {photoUrls[photoType] ? (
          <div className="space-y-2">
            <img 
              src={photoUrls[photoType]} 
              alt={label}
              className="w-24 h-24 object-cover rounded-lg mx-auto"
            />
            <p className="text-sm text-green-600 font-medium flex items-center justify-center gap-1">
              <CheckCircle size={14} /> Photo selected
            </p>
            <p className="text-xs text-gray-500">Click to change</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <Icon className="text-gray-400" size={28} />
            </div>
            <p className="text-sm text-gray-600">{description}</p>
            <p className="text-xs text-gray-400">PNG, JPG or HEIC (max. 5MB)</p>
          </div>
        )}
        {isUploading[photoType] && (
          <div className="mt-2 flex items-center justify-center gap-2 text-amber-600">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-sm">Uploading...</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handlePhotoSelect(e, photoType)}
        className="hidden"
        data-testid={`upload-${photoType}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Button variant="ghost" onClick={() => navigate('/')} className="flex-shrink-0">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3 min-w-0">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
              <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Driver Signup</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8 px-4">
            {[1, 2, 3, 4, 5].map((stepNum) => (
              <div key={stepNum} className="flex items-center flex-1">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  step >= stepNum ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > stepNum ? <CheckCircle size={18} /> : stepNum}
                </div>
                {stepNum < 5 && (
                  <div className={`flex-1 h-1 mx-1 md:mx-2 ${
                    step > stepNum ? 'bg-gradient-to-r from-amber-500 to-yellow-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Step Labels */}
          <div className="flex justify-between mb-6 text-xs text-gray-500 px-2">
            <span className={step >= 1 ? 'text-amber-600 font-medium' : ''}>Personal</span>
            <span className={step >= 2 ? 'text-amber-600 font-medium' : ''}>Vehicle</span>
            <span className={step >= 3 ? 'text-amber-600 font-medium' : ''}>Tax Info</span>
            <span className={step >= 4 ? 'text-amber-600 font-medium' : ''}>ID & Photo</span>
            <span className={step >= 5 ? 'text-amber-600 font-medium' : ''}>Vehicle Photos</span>
          </div>

          <Card className="p-6 md:p-8 border-2">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Personal Information</h2>
                  <p className="text-gray-600">Let's start with your basic details</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input 
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                      placeholder="John Smith"
                      required
                      className="mt-1 border-2"
                      data-testid="input-fullname"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      className="mt-1 border-2"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                      required
                      className="mt-1 border-2"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Create Password *</Label>
                    <Input 
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Enter password"
                      required
                      className="mt-1 border-2"
                      data-testid="input-password"
                    />
                  </div>

                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <Label htmlFor="paypalEmail" className="text-green-800 font-semibold">PayPal Email (Required for Payouts) *</Label>
                    <Input 
                      id="paypalEmail"
                      type="email"
                      value={formData.paypalEmail}
                      onChange={(e) => setFormData({...formData, paypalEmail: e.target.value})}
                      placeholder="your-paypal@email.com"
                      required
                      className="mt-1 border-2 border-green-300 focus:border-green-500"
                      data-testid="input-paypal-email"
                    />
                    <p className="text-xs text-green-700 mt-1">💰 You will receive 70% of every completed trip via PayPal</p>
                  </div>
                  
                  <div>
                    <Label>Service Types *</Label>
                    <p className="text-sm text-gray-500 mb-3">Select the services you want to provide</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleServiceTypeToggle('transportation')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.serviceTypes.includes('transportation')
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="service-transportation"
                      >
                        <Car className={`mb-2 ${formData.serviceTypes.includes('transportation') ? 'text-amber-600' : 'text-gray-400'}`} size={28} />
                        <p className="font-semibold">Transportation</p>
                        <p className="text-xs text-gray-500">Drive passengers</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleServiceTypeToggle('delivery')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.serviceTypes.includes('delivery')
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="service-delivery"
                      >
                        <Package className={`mb-2 ${formData.serviceTypes.includes('delivery') ? 'text-amber-600' : 'text-gray-400'}`} size={28} />
                        <p className="font-semibold">Delivery</p>
                        <p className="text-xs text-gray-500">Deliver packages</p>
                      </button>
                    </div>
                    {formData.serviceTypes.length === 2 && (
                      <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                        <CheckCircle size={14} /> Both services selected - more earning opportunities!
                      </p>
                    )}
                  </div>

                  {/* Referral Code Input */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200">
                    <Label className="text-purple-800 font-semibold flex items-center gap-2">
                      <Gift size={16} /> Referral Code (Optional)
                    </Label>
                    <div className="flex gap-2 mt-2">
                      <Input 
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        placeholder="Enter referral code"
                        className="border-2 border-purple-300 focus:border-purple-500 uppercase"
                        data-testid="input-driver-referral-code"
                      />
                      {isValidatingReferral && <Loader2 className="animate-spin text-purple-500" size={20} />}
                    </div>
                    {referralValidation && (
                      <div className={`mt-2 p-2 rounded-lg ${referralValidation.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        <p className="text-sm flex items-center gap-1">
                          {referralValidation.valid ? <CheckCircle size={14} /> : <XCircle size={14} />}
                          {referralValidation.message}
                        </p>
                        {referralValidation.valid && (
                          <p className="text-xs mt-1">
                            Referred by: {referralValidation.referrer_name}
                          </p>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-purple-600 mt-2">
                      Were you referred by another driver? Enter their code to earn a bonus!
                    </p>
                  </div>

                  {/* Terms & Conditions Checkbox - One-Time Agreement */}
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg border-2">
                    <div className="flex items-start gap-3" data-testid="driver-terms-section">
                      <button
                        type="button"
                        onClick={() => setTermsAccepted(!termsAccepted)}
                        className="mt-0.5 flex-shrink-0"
                        data-testid="driver-terms-checkbox"
                      >
                        {termsAccepted ? (
                          <CheckSquare className="text-amber-500" size={22} />
                        ) : (
                          <Square className="text-gray-400" size={22} />
                        )}
                      </button>
                      <div>
                        <p className="text-sm text-gray-700">
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={openTermsModal}
                            className="text-amber-600 font-semibold underline hover:text-amber-700"
                            data-testid="driver-terms-link"
                          >
                            Terms & Conditions
                          </button>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          This is a one-time agreement required for all drivers
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Vehicle Information */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Vehicle Information</h2>
                  <p className="text-gray-600">Tell us about your vehicle</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="vehicleType">Vehicle Type *</Label>
                    {isLoadingVehicles ? (
                      <div className="mt-1 p-3 border-2 rounded-md flex items-center gap-2 text-gray-500">
                        <Loader2 className="animate-spin" size={16} />
                        Loading vehicle types...
                      </div>
                    ) : (
                      <Select 
                        value={formData.vehicleType}
                        onValueChange={(value) => setFormData({...formData, vehicleType: value})}
                      >
                        <SelectTrigger className="mt-1 border-2" data-testid="select-vehicle-type">
                          <SelectValue placeholder="Select vehicle type" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleTypes.map((type) => (
                            <SelectItem key={type.name} value={type.name}>
                              {type.name} {type.description ? `- ${type.description}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Choose the vehicle type that matches your vehicle
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="vehicleModel">Vehicle Make & Model *</Label>
                    <Input 
                      id="vehicleModel"
                      value={formData.vehicleModel}
                      onChange={(e) => setFormData({...formData, vehicleModel: e.target.value})}
                      placeholder="Toyota Camry"
                      required
                      className="mt-1 border-2"
                      data-testid="input-vehicle-model"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vehicleYear">Year *</Label>
                    <Input 
                      id="vehicleYear"
                      value={formData.vehicleYear}
                      onChange={(e) => setFormData({...formData, vehicleYear: e.target.value})}
                      placeholder="2022"
                      required
                      className="mt-1 border-2"
                      data-testid="input-vehicle-year"
                    />
                  </div>

                  <div>
                    <Label htmlFor="licensePlate">License Plate *</Label>
                    <Input 
                      id="licensePlate"
                      value={formData.licensePlate}
                      onChange={(e) => setFormData({...formData, licensePlate: e.target.value})}
                      placeholder="ABC 1234"
                      required
                      className="mt-1 border-2"
                      data-testid="input-license-plate"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Tax Information */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Tax Information</h2>
                  <p className="text-gray-600">Required for payment processing</p>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CreditCard className="text-blue-600 mt-1" size={24} />
                      <div>
                        <p className="font-semibold text-blue-800">EIN or Social Security Number</p>
                        <p className="text-sm text-blue-700 mt-1">
                          For tax purposes, please provide your full Employer Identification Number (EIN) 
                          or Social Security Number (SSN). This is required for 1099 tax reporting.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="einSsn">EIN or Social Security Number *</Label>
                    <Input 
                      id="einSsn"
                      value={formData.einSsn}
                      onChange={(e) => {
                        // Allow only numbers and hyphens, max 11 chars for SSN format XXX-XX-XXXX
                        const value = e.target.value.replace(/[^0-9-]/g, '');
                        setFormData({...formData, einSsn: value});
                      }}
                      placeholder="Enter full EIN (XX-XXXXXXX) or SSN (XXX-XX-XXXX)"
                      required
                      className="mt-1 border-2"
                      maxLength={15}
                      inputMode="numeric"
                      data-testid="input-ein-ssn"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter your full 9-digit EIN or Social Security Number. This is required for tax purposes and stored securely.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Profile Photo & License */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Profile & License</h2>
                  <p className="text-gray-600">Upload your photo and driver's license</p>
                </div>

                <div className="grid gap-6">
                  <PhotoUploadBox
                    label="Profile Photo"
                    photoType="profilePhoto"
                    inputRef={profilePhotoRef}
                    icon={User}
                    description="Take a clear photo of your face"
                  />
                  
                  <PhotoUploadBox
                    label="Driver's License"
                    photoType="licensePhoto"
                    inputRef={licensePhotoRef}
                    icon={FileText}
                    description="Photo of your valid driver's license"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> Your profile photo will be shown to passengers so they can identify you. 
                    Make sure both photos are clear and readable.
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Vehicle Photos */}
            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Vehicle Photos</h2>
                  <p className="text-gray-600">Help passengers identify your vehicle</p>
                </div>

                <div className="grid gap-6">
                  <PhotoUploadBox
                    label="Front of Vehicle"
                    photoType="vehicleFront"
                    inputRef={vehicleFrontRef}
                    icon={Car}
                    description="Clear photo of your vehicle from the front"
                  />
                  
                  <PhotoUploadBox
                    label="Rear of Vehicle (with License Plate)"
                    photoType="vehicleRear"
                    inputRef={vehicleRearRef}
                    icon={ImageIcon}
                    description="Rear view with license plate clearly visible"
                  />
                </div>

                <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                  <p className="text-sm text-green-900">
                    <strong>Important:</strong> Make sure the license plate is clearly visible in the rear photo. 
                    This helps passengers confirm they're getting into the right vehicle.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {step > 1 && step <= 2 && (
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handlePrevStep}
                  className="flex-1 border-2"
                  data-testid="btn-back"
                >
                  Back
                </Button>
              )}
              <Button 
                onClick={handleNextStep}
                disabled={isSubmitting}
                className={`flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 ${step === 1 ? 'w-full' : ''}`}
                data-testid="btn-continue"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {step >= 4 ? 'Uploading...' : 'Processing...'}
                  </>
                ) : (
                  step === 5 ? 'Complete Registration' : 'Continue'
                )}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="driver-terms-modal">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden relative animate-in fade-in zoom-in-95">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="text-amber-500" size={24} />
                <h2 className="text-xl font-bold">{termsContent.title}</h2>
              </div>
              <button 
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-driver-terms-modal"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingTerms ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-amber-500" size={32} />
                </div>
              ) : termsContent.content ? (
                <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                  {termsContent.content}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Terms & Conditions have not been set up yet. Please contact support.
                </p>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600"
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                data-testid="driver-accept-terms-btn"
              >
                <CheckCircle size={16} className="mr-2" />
                I Accept
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DriverSignup;
